'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import service from '../../../service';
import { cn } from '../../../utils';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { CircleAlert } from 'lucide-react';
import ColorPicker from '@Components/ColorPicker';
import { categoryIcons } from '@Constants/iconMap';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';

const FlowCategorySchema = z.object({
  name: z.string().min(2, 'Please enter at least 2 characters'),
  color: z.string().max(20, 'Keep it under 20 characters'),
  icon: z.string().max(20, 'Keep it under 20 characters'),
});

export type FlowCategoryFormValues = z.infer<typeof FlowCategorySchema>;

type Props = {
  className?: string;
  defaultValues?: Partial<FlowCategoryFormValues>;
  submitLabel?: string;
  /** Invalidate this query key after success */
  invalidateKey?: (string | number)[];
  onSuccess?: (created?: unknown) => void;

  /** NEW: form id so parent buttons can submit with form={formId} */
  formId?: string;
  /** NEW: hide internal Reset/Submit buttons (parent supplies them) */
  hideInternalActions?: boolean;
  /** When provided, switches to edit mode and calls updateFlowCategory */
  editId?: string;
};

export default function FlowCategoryForm({
  className,
  defaultValues,
  submitLabel,
  invalidateKey = ['flow-categories'],
  onSuccess,

  formId = 'flow-category-form',
  hideInternalActions = false,
  editId,
}: Props) {
  const queryClient = useQueryClient();
  const isEditMode = !!editId;
  const resolvedSubmitLabel =
    submitLabel ?? (isEditMode ? 'Update category' : 'Create category');

  const form = useForm<FlowCategoryFormValues>({
    resolver: zodResolver(FlowCategorySchema),
    defaultValues: { name: '', color: '', icon: '', ...defaultValues },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    reset,
    setError,
    formState: { isSubmitting },
  } = form;

  const mutation = useMutation({
    mutationFn: async (values: FlowCategoryFormValues) => {
      const payload = {
        name: values.name.trim(),
        color: values.color?.trim() || undefined,
        icon: values.icon?.trim() || undefined,
      };
      if (isEditMode) {
        return service.updateFlowCategory(editId, payload);
      }
      return service.createFlowCategory(payload);
    },
    onSuccess: async (result) => {
      if (invalidateKey?.length) {
        await queryClient.invalidateQueries({ queryKey: invalidateKey });
      }
      onSuccess?.(result);
      toastSuccess(isEditMode ? 'Category updated' : 'Category created');
      if (!isEditMode) {
        reset({ name: '', color: '', icon: '' });
      }
    },
    onError: (err: any) => {
      setError('root', {
        type: 'server',
        message:
          err?.message ||
          `Something went wrong while ${isEditMode ? 'updating' : 'creating'} the category. Please try again.`,
      });
    },
  });

  const onSubmit = (values: FlowCategoryFormValues) => {
    mutation.mutate(values);
  };

  const pending = isSubmitting || mutation.isPending;

  return (
    <div className={cn('max-w-xl', className)}>
      <Form {...form}>
        {/* IMPORTANT: expose id for external submit via <Button form={formId}/> */}
        <form
          id={formId}
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <FormField
            control={control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., General" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="color"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Color</FormLabel>
                <FormControl>
                  <ColorPicker
                    initialColor={field.value}
                    variant="background"
                    updateColor={(newColor) => field.onChange(newColor)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="icon"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Icon</FormLabel>

                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an icon" />
                    </SelectTrigger>
                  </FormControl>

                  <SelectContent>
                    {Object.entries(categoryIcons).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          {value.icon}
                          <span>{value.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <FormMessage />
              </FormItem>
            )}
          />

          {/* <FormField
            control={control}
            name="icon"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Icon</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., Calendar" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          /> */}

          {/* top-level error */}
          {form.formState.errors.root?.message ? (
            <Alert variant="destructive" className="my-4">
              <CircleAlert />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {form.formState.errors.root?.message ??
                  'Error creating category. Please try again later.'}
              </AlertDescription>
            </Alert>
          ) : null}

          {!hideInternalActions && (
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => reset()}
                disabled={pending}
              >
                Reset
              </Button>
              <Button type="submit" variant="primary" loading={pending}>
                {resolvedSubmitLabel}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
}
